'use client';

// Motor de recomendaciones agrupadas de /reservas (extraído de
// AvailabilitySearch en F11b del plan maestro — markup y lógica idénticos).
// Cuando la búsqueda es de grupo grande, propone la combinación de
// apartamentos del mismo edificio (o más cercanos) que cubre los huéspedes.

import { CheckCircle2, Navigation, Sparkles } from 'lucide-react';
import { useMemo } from 'react';
import type { EnrichedAvailability } from '@/lib/beds24/types';
import { getDisplayName } from '@/types/apartment';
import type { Apartment } from '@/types/apartment';
import type { CTAEventProps } from '@/lib/analytics/posthog';
import { fireWspReservasConversion } from '@/lib/analytics/gtag-conversion';
import { openTrackedWhatsApp } from '@/lib/whatsapp/open-tracked-whatsapp';
import WhatsAppGlyph from '@/components/whatsapp/WhatsAppGlyph';
import { companyConfig } from '@/lib/config-loader';

export interface RecommendedGroupOption {
  items: EnrichedAvailability[];
  totalCapacity: number;
  totalPrice: number;
  proximityLabel: string;
  proximityDetail: string;
}

function formatMoney(value: number): string {
  return `$${Math.round(value).toLocaleString('es-CO')}`;
}

export function formatShortDate(value: string): string {
  if (!value) return '';
  return new Date(`${value}T00:00:00`).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
  });
}

function getBuildingKey(apartment: Apartment): string {
  const building = apartment.buildingId || apartment.location?.building;
  return building ? building.toLowerCase().trim() : '';
}

export function buildRecommendedGroupOption(
  apartments: EnrichedAvailability[],
  requestedGuests: number
): RecommendedGroupOption | null {
  if (apartments.length < 2 || requestedGuests < 2) return null;

  const sorted = [...apartments].sort((a, b) => {
    const ratingA = a.apartment.reviewStats?.avgRating ?? 0;
    const ratingB = b.apartment.reviewStats?.avgRating ?? 0;
    if (ratingA !== ratingB) return ratingB - ratingA;
    return b.apartment.capacity.guests - a.apartment.capacity.guests;
  });

  const pickGreedy = (candidates: EnrichedAvailability[]) => {
    const chosen: EnrichedAvailability[] = [];
    let totalCapacity = 0;
    for (const item of candidates) {
      if (totalCapacity >= requestedGuests) break;
      chosen.push(item);
      totalCapacity += item.apartment.capacity.guests;
    }
    return totalCapacity >= requestedGuests ? chosen : null;
  };

  const byBuilding = new Map<string, EnrichedAvailability[]>();
  for (const item of sorted) {
    const key = getBuildingKey(item.apartment);
    if (!key) continue;
    byBuilding.set(key, [...(byBuilding.get(key) ?? []), item]);
  }

  let chosen: EnrichedAvailability[] | null = null;
  let sameBuilding = false;
  for (const candidates of byBuilding.values()) {
    const attempt = pickGreedy(candidates);
    if (!attempt) continue;
    if (
      !chosen ||
      attempt.length < chosen.length ||
      attempt.reduce((sum, item) => sum + item.totalPrice, 0) <
        chosen.reduce((sum, item) => sum + item.totalPrice, 0)
    ) {
      chosen = attempt;
      sameBuilding = true;
    }
  }

  if (!chosen || chosen.length < 2) return null;

  const floors = chosen
    .map((item) => item.apartment.piso)
    .filter((floor): floor is number => Number.isFinite(floor))
    .sort((a, b) => a - b);
  const totalCapacity = chosen.reduce(
    (sum, item) => sum + item.apartment.capacity.guests,
    0
  );
  const totalPrice = chosen.reduce((sum, item) => sum + item.totalPrice, 0);
  const floorText =
    floors.length === 1
      ? `Piso ${floors[0]}`
      : `Piso ${floors.slice(0, -1).join(', ')} y Piso ${floors[floors.length - 1]}`;

  return {
    items: chosen,
    totalCapacity,
    totalPrice,
    proximityLabel: `Mismo edificio · ${floorText}`,
    proximityDetail: 'Llegan juntos, suben por el mismo lobby y quedan a pocos pisos.',
  };
}

export function RecommendedGroupCard({
  option,
  requestedGuests,
  arrival,
  departure,
  ctaAttribution,
}: {
  option: RecommendedGroupOption;
  requestedGuests: number;
  arrival: string;
  departure: string;
  ctaAttribution: CTAEventProps;
}) {
  const apartmentNames = option.items.map((item) => getDisplayName(item.apartment));
  const whatsappMessage = [
    `Hola, somos ${requestedGuests} personas y queremos reservar la opción recomendada.`,
    `Fechas: ${formatShortDate(arrival)} al ${formatShortDate(departure)}.`,
    `Apartamentos: ${apartmentNames.join(' + ')}.`,
    `Capacidad total: ${option.totalCapacity} personas.`,
    `Total estimado: ${formatMoney(option.totalPrice)} COP.`,
  ].join(' ');
  const whatsappHref = useMemo(
    () =>
      `https://wa.me/${companyConfig.contact.whatsapp.number}?text=${encodeURIComponent(whatsappMessage)}`,
    [whatsappMessage]
  );

  return (
    <section className="mb-8 overflow-hidden rounded-3xl border border-emerald-200 bg-emerald-50/70 shadow-[0_16px_45px_-35px_rgba(6,95,70,0.55)]">
      <div className="grid gap-0 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="p-5 sm:p-6">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 px-3 py-1 text-xs font-semibold text-white">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.25} />
              Opción recomendada
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-900 ring-1 ring-emerald-100">
              <Navigation className="h-3.5 w-3.5" strokeWidth={2.25} />
              {option.proximityLabel}
            </span>
          </div>

          <h3 className="text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">
            Grupo de {requestedGuests}: juntos, sin partir el viaje
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-700">
            {option.proximityDetail} Esta combinación cubre hasta{' '}
            <span className="font-semibold text-slate-950">
              {option.totalCapacity} personas
            </span>
            .
          </p>

          <div className="mt-5 space-y-2">
            {option.items.map((item) => (
              <div
                key={item.apartment.id}
                className="flex items-start gap-2 rounded-2xl bg-white/80 px-3.5 py-3 text-sm text-slate-700 ring-1 ring-emerald-100"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" strokeWidth={2.25} />
                <p>
                  <span className="font-semibold text-slate-950">
                    {getDisplayName(item.apartment)}
                  </span>{' '}
                  · {item.apartment.capacity.guests} personas · Piso {item.apartment.piso}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-emerald-100 bg-white p-5 sm:p-6 lg:border-l lg:border-t-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Total estimado
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-950">
            {formatMoney(option.totalPrice)}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            Suma de los apartamentos disponibles para tus fechas. Te confirmamos el anticipo y
            logística por WhatsApp.
          </p>
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => {
              event.preventDefault();
              openTrackedWhatsApp(whatsappMessage, {
                ...ctaAttribution,
                cta_position: 'reservas_recommended_group',
                cta_label: 'Cotizar esta opción',
                apartment_id: option.items.map((item) => item.apartment.id).join(','),
                apartment_name: apartmentNames.join(' + '),
                has_pricing: true,
              });
              fireWspReservasConversion({
                cta_position: 'reservas_recommended_group',
                arrival: ctaAttribution.arrival,
                departure: ctaAttribution.departure,
                num_adults: ctaAttribution.num_adults,
                apartment_id: option.items.map((item) => item.apartment.id).join(','),
              });
            }}
            className="mt-5 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-wsp-brand px-5 py-3 text-sm font-semibold text-wsp-ink shadow-sm transition hover:-translate-y-px hover:bg-wsp-brand-dark active:translate-y-0"
          >
            <WhatsAppGlyph size={18} />
            Cotizar esta opción
          </a>
        </div>
      </div>
    </section>
  );
}
